import { getObservableProductRoute } from 'tio-employee/services/observability';
import { getStatus } from 'tio-common/helpers/observability';
import { Section, VStack } from 'tio-ui/components/layout';
import PageHeader from 'tio-ui/components/page-header';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import ActionableTranslation from 'tio-ui/components/actionable-translation';
import Component from '@glimmer/component';
import ExtractionSummary from 'tio-employee/components/observability/extraction-summary';
import ObservabilityConfirmController from 'tio-employee/controllers/authenticated/observability/upload';
import type { ObservableSource } from 'tio-employee/services/observability';
import type IntlService from 'ember-intl/services/intl';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import { PhFileArrowUp, PhEnvelope } from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';
import type { ChipSignature } from 'tio-ui/components/chip';

interface ObservabilityConfirmSignature {
  Args: {
    controller: ObservabilityConfirmController;
    model: ObservableDocumentModel;
  };
}

const getStatusIntent = (status: string): ChipSignature['Args']['intent'] => {
  switch (status) {
    case 'PROCESSING':
      return 'primary';
    case 'INVALID':
    case 'FAILED':
      return 'danger';
    case 'PROCESSED':
    case 'APPLIED':
      return 'success';
    case 'IN_DISPUTE':
    case 'PARTIALLY_APPLIED':
      return 'warning';
  }
};

const getSubmittedIcon = (via: string) => {
  if (via === 'EMAIL') {
    return PhEnvelope;
  }
  return PhFileArrowUp;
};

export default class ObservabilityConfirm extends Component<ObservabilityConfirmSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;

  get productSource(): ObservableSource {
    return <ObservableSource>this.args.controller.source;
  }

  // `syf_enrollment` is a valid `?source=` value but isn't one of the
  // OBSERVABLE_PRODUCT_ROUTES keys that ObservableSource narrows to, so compare
  // the raw source string here.
  get isSyfEnrollmentSource(): boolean {
    return this.args.controller.source === 'syf_enrollment';
  }

  get documentCreated() {
    const observableDocument = this.args.model;
    const createdAtDate = new Date(observableDocument.createdAt);
    const date = createdAtDate.toLocaleDateString();
    const time = createdAtDate.toLocaleTimeString();
    const via = this.intl.t(`observability.${observableDocument.via}`);
    return { via, date, time };
  }

  get employeeEmail() {
    return this.sessionContext.user?.primaryEmail?.email;
  }

  get subtitleSections() {
    const result = this.intl.t('observability.confirmation.subtitle', {
      email: this.employeeEmail,
    });
    return result.split('\n');
  }

  get failedSubtitleSections() {
    const result = this.intl.t('observability.confirmation.subtitle', {
      documentType: this.documentTypeName,
    });
    return result.split('\n');
  }

  get documentType(): 'nslds' | 'statement' | 'transaction_history' | undefined {
    const observableDocument = this.args.model;
    switch (observableDocument.provider) {
      case 'ObservableProvider.STATEMENT':
        return 'statement';
      case 'ObservableProvider.NSLDS':
        return 'nslds';
      case 'ObservableProvider.ACCOUNT_TXN_HISTORY':
        return 'transaction_history';
    }
  }

  get documentTypeName() {
    switch (this.documentType) {
      case 'statement':
        return this.intl.t('observability.statement');
      case 'transaction_history':
        return this.intl.t('observability.transaction_history');
      case 'nslds':
        return this.intl.t('observability.nslds');
      default:
        return this.intl.t('observability.default_document');
    }
  }

  get transactionHistoryFailed() {
    const observableDocument = this.args.model;

    const conditions = [
      ['syf_payments'].includes(this.productSource),
      observableDocument.extractionState === 'ExtractionState.PROCESSED',
      observableDocument.reflectionState === 'ReflectionState.NOT_APPLIED',
    ];
    return conditions.every(Boolean);
  }

  get manualTransactionUrl() {
    // only SYF for now; generate URLS for other products here if necessary
    return this.router.urlFor('authenticated.syf.payments.new');
  }

  get showFurtherReviewStatement() {
    const observableDocument = this.args.model;
    return [
      observableDocument.provider === 'ObservableProvider.ACCOUNT_STATEMENT',
      observableDocument.reflectionState === 'ReflectionState.PARTIALLY_APPLIED',
    ].every(Boolean);
  }

  get showSubtitle() {
    const observableDocument = this.args.model;
    return observableDocument.provider === 'ObservableProvider.ACCOUNT_TXN_HISTORY';
  }

  get documentPartiallyApplied() {
    const observableDocument = this.args.model;
    return observableDocument.reflectionState === 'ReflectionState.PARTIALLY_APPLIED';
  }

  get isSlr() {
    return this.args.model.person?.employees?.some((employee) => {
      return employee.isActive && !!employee.plan && employee.plan?.cadence !== 'NEVER';
    });
  }

  <template>
    <VStack>
      {{#if this.transactionHistoryFailed}}
        <PageHeader
          @title={{t "observability.confirmation.failed_title" documentType=this.documentTypeName}}
          @sticky={{false}}
        />
        <Section>
          <:body>
            <p class="my-4">{{t
                "observability.confirmation.failed_subtitle"
                documentType=this.documentTypeName
              }}</p>
            <p class="my-4"><ActionableTranslation
                @t="observability.confirmation.manual_prompt"
                @links={{hash manualPaymentsLink=(hash t="here" href=this.manualTransactionUrl)}}
              /></p>
          </:body>
        </Section>
      {{else}}
        <PageHeader
          @title={{t "observability.confirmation.title" provider=this.documentType}}
          @subtitle={{t "observability.confirmation.header" provider=this.documentType}}
          @sticky={{false}}
        />
        <Section>
          <:body>
            {{#if this.showSubtitle}}
              <p class="mt-2">
                {{#each this.subtitleSections as |section|}}
                  <span class="block mt-2">{{section}}</span>
                {{/each}}
              </p>
            {{/if}}
            <div class="font-semibold mt-4">
              {{@model.observableAsset.filename}}
              <Chip @intent={{getStatusIntent (getStatus @model)}} class="ml-4">
                {{t "observable_document.status" state=(getStatus @model)}}
              </Chip>
            </div>
            <p class="tio-row-flush italic mt-2 mb-4">
              {{#let (getSubmittedIcon @model.via) as |SubmittedIcon|}}
                <SubmittedIcon @size="24" class="mr-2" />
              {{/let}}
              {{t
                "observability.dispute.document.uploaded"
                via=this.documentCreated.via
                date=this.documentCreated.date
                time=this.documentCreated.time
              }}
            </p>
            <ExtractionSummary @document={{@model}} class="w-full md:w-2/3" />
            <footer>
              {{#if this.documentPartiallyApplied}}
                <p class="my-8">
                  {{t "observability.document.summary.partially_applied"}}
                </p>
              {{/if}}
              {{#if this.showFurtherReviewStatement}}
                <p class="my-8">
                  {{t "observability.document.summary.partially_applied"}}
                </p>
              {{/if}}
              {{#if this.isSyfEnrollmentSource}}
                <LinkTo
                  @route="authenticated.syf.linking-confirmation"
                  @model={{@model.financialInstitution}}
                  class="tio-btn tio-btn--primary tio-btn--md"
                >
                  {{t "observability.confirmation.continue" source=this.productSource}}
                </LinkTo>
              {{else if (eq this.productSource "syf_payments")}}
                <LinkTo
                  @route="authenticated.syf.dashboard"
                  class="tio-btn tio-btn--primary tio-btn--md"
                >
                  {{t "observability.confirmation.continue" source=this.productSource}}
                </LinkTo>
              {{else}}
                <LinkTo
                  @route={{getObservableProductRoute this.productSource this.isSlr}}
                  class="tio-btn tio-btn--primary tio-btn--md"
                >
                  {{t "observability.confirmation.continue" source=this.productSource}}
                </LinkTo>
              {{/if}}
            </footer>
          </:body>
        </Section>
      {{/if}}
    </VStack>
  </template>
}
